import { useState, useEffect } from 'react';
import { Link2, Wand2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalFooter, Button, Input, PlatformIcon } from '@/components/ui';
import { detectPlatform, getPlatformConfig, isMusicPlatform } from '@/utils/platformDetector';
import MusicMatchPanel from './MusicMatchPanel';
import type { Platform, MusicMatchResult } from '@/types';

interface AddLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    originalUrl: string;
    title?: string;
    musicMatches?: MusicMatchResult[];
  }) => Promise<void>;
}

const AddLinkModal = ({ isOpen, onClose, onSubmit }: AddLinkModalProps) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [detectedPlatform, setDetectedPlatform] = useState<Platform | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMatches, setSelectedMatches] = useState<MusicMatchResult[]>([]);
  const [showMusicMatch, setShowMusicMatch] = useState(false);

  useEffect(() => {
    if (url.length > 5) {
      const platform = detectPlatform(url);
      setDetectedPlatform(platform);
      setShowMusicMatch(platform !== 'generic' && isMusicPlatform(platform));
    } else {
      setDetectedPlatform(null);
      setShowMusicMatch(false);
    }
  }, [url]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!url.trim()) {
      setError(t('createModal.pleaseEnterUrl'));
      return;
    }

    if (!title.trim()) {
      setError(t('linkPages.titleRequired'));
      return;
    }

    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
    } catch {
      setError(t('createModal.pleaseEnterValidUrl'));
      return;
    }

    setIsLoading(true);
    try {
      const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;
      await onSubmit({
        originalUrl: normalizedUrl,
        title: title.trim() || undefined,
        musicMatches: selectedMatches.length > 0 ? selectedMatches : undefined,
      });
      handleClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('DAILY_QUOTA_REACHED')) {
        const quota = msg.split(':')[1];
        setError(t('createModal.dailyQuotaReached', { quota }));
      } else {
        setError(t('linkPages.failedToAddLink'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setUrl('');
    setTitle('');
    setDetectedPlatform(null);
    setError(null);
    setSelectedMatches([]);
    setShowMusicMatch(false);
    onClose();
  };

  const platformConfig = detectedPlatform ? getPlatformConfig(detectedPlatform) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('linkPages.addLink')}
      description={t('linkPages.addLinkDescription')}
      size="md"
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Input
              label={t('linkPages.urlLabel')}
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://open.spotify.com/track/..."
              leftIcon={<Link2 className="w-5 h-5" />}
              autoFocus
            />

            {platformConfig && detectedPlatform !== 'generic' && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-lime-50 dark:bg-lime-900/20 rounded-xl">
                <Wand2 className="w-4 h-4 text-lime-600" />
                <span className="text-sm text-lime-700 dark:text-lime-400">
                  {t('createModal.detected')} <strong>{platformConfig.name}</strong>
                </span>
                <div className="ml-auto">
                  <PlatformIcon platform={detectedPlatform!} size="sm" />
                </div>
              </div>
            )}
          </div>

          <Input
            label={t('linkPages.linkTitle')}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('linkPages.linkTitlePlaceholder')}
            hint={t('linkPages.linkTitleHint')}
          />

          {showMusicMatch && (
            <MusicMatchPanel
              url={url.startsWith('http') ? url : `https://${url}`}
              onMatchesChange={setSelectedMatches}
            />
          )}
        </div>

        <ModalFooter>
          <Button variant="ghost" size="md" onClick={handleClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            isLoading={isLoading}
            leftIcon={!isLoading ? <Link2 className="w-4 h-4" /> : undefined}
          >
            {isLoading ? t('common.loading') : t('linkPages.addLink')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AddLinkModal;
